import "./style/TipTap.scss";
import { EditorContent, Editor } from "@tiptap/react";
import { useEffect } from "react";
import useTextFilesStore from "../../stores/textFilesStore";
import debounce from "../../utility/debounce";
import useDataStore from "../../stores/dataStore";
import { useGetEditor } from "../../context/useEditorContext";
import useMainStore from "../../stores/mainStore";
import { history } from "@tiptap/pm/history";
import ManageLinkHover from "./manageLinkHover";

/**
 * Resets the editor history by unregistering the history plugin and then registering it again.
 */
function resetEditorHistory(editor: Editor | null) {
    editor?.unregisterPlugin("history");
    editor?.registerPlugin(history());
}

// Save on a debounce so it doesn't save on every keystroke
const debouncedCallback = debounce(
    (selectedNoteID: string, content: string) => {
        // Save the text file
        useTextFilesStore.getState().saveContent(selectedNoteID, content);
    },
    2000
);

export default function Tiptap({
    // onDrop,
    onBlur,
    onUpdate,
    onPaste,
}: {
    onBlur?: (htmlText: string, rawText: string) => void;
    onUpdate?: (htmlText: string, rawText: string) => void;
    // onDrop?: (event: React.DragEvent<HTMLDivElement>, editor: any) => void;
    onPaste?: (event: ClipboardEvent) => void;
    onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}) {
    // Store
    const selectedNoteID = useMainStore((state) => state.data.selectedNoteID);

    // Ref/Context
    const editor = useGetEditor().editor.current;

    // Set initial HTML
    useEffect(() => {
        if (!editor) return;

        async function loadInitialContent() {
            try {
                const textFile = await useTextFilesStore
                    .getState()
                    .getTextFile(selectedNoteID);

                if (textFile?.content === undefined || textFile?.content === null) {
                    useMainStore.getState().set.selectedNoteID(() => undefined);
                    
                    // // Initialize empty editor
                    // editor?.commands.setContent("", false);
                    // editor?.commands.focus();
                    // editor?.commands.setTextSelection({
                    //     from: 1,
                    //     to: 1,
                    // });
                    // console.log("No content found, initialized empty editor");
                } else {
                    // Load existing content
                    editor.commands.setContent(textFile.content, true);
                    resetEditorHistory(editor);
                }
            } catch (error) {
                console.error("Failed to load note content:", error);
                // Set to empty editor on error
                editor.commands.setContent("", false);
            }
        }

        loadInitialContent();
    }, [selectedNoteID, editor]);

    // Setup event listeners
    useEffect(() => {
        if (!editor) return;

        const handleBlur = ({ editor }: { editor: Editor }) => {
            return handleEditorBlur(selectedNoteID, editor, onBlur);
        };

        const handleError = (props: { error: Error; editor: Editor }) => {
            handleEditorError(props);
        };

        const handleUpdate = ({ editor }: { editor: Editor }) => {
            handleEditorUpdate(
                selectedNoteID,
                editor,
                debouncedCallback,
                onUpdate
            );
        };

        editor.on("contentError", handleError);
        editor.on("update", handleUpdate);
        editor.on("blur", handleBlur);

        return () => {
            editor.off("contentError", handleError);
            editor.off("update", handleUpdate);
            editor.off("blur", handleBlur);
        };
    }, [editor, selectedNoteID, onBlur, onUpdate]);

    // Cancel pending debounced saves on unmount to prevent memory leaks
    useEffect(() => {
        return () => {
            // Cancel any pending debounced save operations
            debouncedCallback.cancel();
        };
    }, []);

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    };

    return (
        <div
            className="flex h-full w-full flex-1 flex-col"
            onDragOver={handleDragOver}
            onDrop={handleDrop}
        >
            <EditorContent
                editor={editor}
                style={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                }}
            />
            <ManageLinkHover />
        </div>
    );
}

// Events
/**
 * Handles editor blur event
 */
const handleEditorBlur = (
    selectedNoteID: string,
    editor: Editor,
    onBlur?: (htmlText: string, rawText: string) => void
) => {
    useTextFilesStore.getState().saveContent(selectedNoteID, editor.getHTML());
    return onBlur?.(editor.getHTML(), editor.getText());
};

/**
 * Handles editor errors
 */
const handleEditorError = (props: { error: Error; editor: Editor }) => {
    console.log(props);
};

/**
 * Handles editor content updates
 */
const handleEditorUpdate = (
    selectedNoteID: string,
    editor: Editor,
    debouncedCallback: (selectedNoteID: string, content: string) => void,
    onUpdate?: (htmlText: string, rawText: string) => void
) => {
    const changesMade =
        useTextFilesStore.getState().textFiles[selectedNoteID]?.content !==
        editor.getHTML();

    if (!changesMade) return;

    // Update changesMade status
    setTimeout(() => {
        if (
            changesMade &&
            !useDataStore.getState().data.notes[selectedNoteID]?.changesMade
        ) {
            useDataStore.getState().set.note(
                selectedNoteID,
                {
                    changesMade: true,
                },
                true
            );
        }
    }, 100);

    const editorHTML = editor.getHTML();
    debouncedCallback(selectedNoteID, editorHTML);
    return onUpdate?.(editorHTML, editor.getText());
};
